import { type DirectHotelProvider, type HotelProvider, KayakHotelProvider } from '@/interfaces';

import { hasVisBookMapping } from './visbook';
import { hasThonMapping } from '@/utils/integration/thon';

const revatoKayakProvider: HotelProvider = {
    code: KayakHotelProvider.REVATO,
    name: 'Revato',
    logo: 'https://content.r9cdn.net/rimg/provider-logos/hotels/h/REVATODIRECT.png?crop=false&width=88&height=31&fallback=default1.png&_v=9058b20d452c1e7d70f003bb4283b1fc',
    isDirect: false,
    nameOverrideOutPage: 'Hotellets partner',
    nameOverrideSearchResults: 'Hotellets partner',
};

const strawberryHotelProvider: HotelProvider = {
    code: KayakHotelProvider.STRAWBERRY,
    name: 'Strawberry',
    logo: require('../../public/images/logos/rectangular/strawberry.png').default?.src,
    isDirect: true,
};

const visbookHotelProvider: HotelProvider = {
    code: 'VISBOOK',
    name: 'Visbook',
    isDirect: false,
};

const kayakProviders: Record<KayakHotelProvider, HotelProvider> = {
    REVATO: revatoKayakProvider,
    CHOICENORDIC: strawberryHotelProvider,
};

export const providers: Record<DirectHotelProvider, HotelProvider> = {
    // Meta - parent providers for the concrete direct providers
    VISBOOK: {
        code: 'VISBOOK',
        name: 'VisBook',
        isDirect: false,
    },
    // VisBook
    CITYBOX: {
        code: 'CITYBOX',
        name: 'Citybox',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/citybox.svg').default?.src,
    },
    SMARTHOTEL: {
        code: 'SMARTHOTEL',
        name: 'Smarthotel',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/smarthotel.png').default?.src,
    },
    CLASSICNORWAY: {
        code: 'CLASSICNORWAY',
        name: 'Classic Norway',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/classic-norway-logo.svg').default?.src,
    },
    RONDANEHFH: {
        code: 'RONDANEHFH',
        name: 'Rondane Høyfjellshotell',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/rondane.svg').default?.src,
    },
    SKASLIEN: {
        code: 'SKASLIEN',
        name: 'Skaslien Gjestgiveri',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/skaslien.png').default?.src,
    },
    FJAERLANDFJORDSTUE: {
        code: 'FJAERLANDFJORDSTUE',
        name: 'Fjærland Fjordstue',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/placeholder.svg').default?.src,
    },
    MARKENGJESTEHUS: {
        code: 'MARKENGJESTEHUS',
        name: 'Marken Gjestehus',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/marken-gjestehus.svg').default?.src,
    },
    BOOKLOFOTEN: {
        code: 'BOOKLOFOTEN',
        name: 'Book Lofoten',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/book-lofoten.png').default?.src,
    },
    BYRKJEDALSTUNET: {
        code: 'BYRKJEDALSTUNET',
        name: 'Byrkjedalstunet',
        parentProvider: visbookHotelProvider,
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/byrkjedalstunet.png').default?.src,
    },
    THON: {
        code: 'THON',
        name: 'Thon Hotels',
        isDirect: true,
        logo: require('../../public/images/logos/rectangular/thonlogo.png').default?.src,
    },
};

export const hasExtraHotelData = (hotelFilename: string): boolean => hasThonMapping(hotelFilename);

export const hasDirectMapping = (hotelId: number): boolean => hasThonMapping(hotelId) || hasVisBookMapping(hotelId);

export const getHotelProvider = (providerCode: KayakHotelProvider | DirectHotelProvider): HotelProvider | undefined =>
    providers[providerCode] || kayakProviders[providerCode];

export const supportsIntegratedBooking = (providerCode: DirectHotelProvider): boolean => {
    switch (providerCode) {
        // VisBook
        case 'VISBOOK':
        case 'SMARTHOTEL':
        case 'CITYBOX':
        case 'CLASSICNORWAY':
        case 'RONDANEHFH':
        case 'SKASLIEN':
        case 'FJAERLANDFJORDSTUE':
        case 'MARKENGJESTEHUS':
        case 'BOOKLOFOTEN':
        case 'BYRKJEDALSTUNET':
            return true;
        case 'THON':
            return true;
        default:
            return false;
    }
};
